// @ts-nocheck - Temporary fix for backend integration types
import { httpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';
import type { JobChat, ChatMessage } from '@/types';

export class BackendCommunicationService {
  private wsConnection: WebSocket | null = null;
  private listeners: ((chats: JobChat[]) => void)[] = [];

  private getAuthToken(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('auth_token') || undefined;
  }

  private getUserId(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('user_id') || undefined;
  }

  // Helper method to handle API calls with graceful 404 handling
  private async safeApiCall<T>(
    apiCall: () => Promise<any>,
    fallbackValue: T,
    serviceName: string = 'Communication service'
  ): Promise<T> {
    try {
      const response = await apiCall();
      
      if (!response.success) {
        // Check if it's a 404 error (service not implemented)
        if (response.error?.includes('404') || response.error?.includes('not found')) {
          console.warn(`💬 ${serviceName} not available yet - using fallback`);
          return fallbackValue;
        }
        throw new Error(response.error || `${serviceName} request failed`);
      }
      
      return response.data || fallbackValue;
    } catch (error: any) {
      // Handle 404 errors gracefully
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        console.warn(`💬 ${serviceName} not available - using fallback`);
        return fallbackValue;
      }
      throw error;
    }
  }

  // WebSocket connection for real-time messaging
  connectWebSocket(): void {
    const token = this.getAuthToken();
    if (!token) {
      console.log('🔌 WebSocket: No auth token available');
      return;
    }

    // Check if WebSocket is enabled
    if (import.meta.env.VITE_ENABLE_WEBSOCKET !== 'true') {
      console.log('🔌 WebSocket: Disabled via environment variable');
      return;
    }

    // Build proper WebSocket URL using the API config
    const baseUrl = API_CONFIG.BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsUrl = `${baseUrl}/api/v1/auth/communication/ws?token=${token}`;
    
    console.log('🔌 Connecting to WebSocket:', wsUrl);
    this.wsConnection = new WebSocket(wsUrl);

    this.wsConnection.onopen = () => {
      console.log('✅ WebSocket connected successfully');
    };

    this.wsConnection.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.handleWebSocketMessage(data);
    };

    this.wsConnection.onclose = (event) => {
      console.log('🔌 WebSocket disconnected', { code: event.code, reason: event.reason });
      // Attempt to reconnect after 5 seconds if not a clean close
      if (event.code !== 1000) {
        setTimeout(() => this.connectWebSocket(), 5000);
      }
    };

    this.wsConnection.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };
  }

  disconnectWebSocket(): void {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
  }

  private handleWebSocketMessage(data: any): void {
    // Handle real-time message updates
    if (data.type === 'new_message') {
      this.notifyListeners();
    }
    
    // Handle real-time notification updates
    if (data.type === 'notification') {
      // Notification received via WebSocket
      // The NotificationsDropdown component will fetch updated list on next poll
      console.log('📬 Real-time notification received:', data.payload);
    }
    
    // Handle unread count updates
    if (data.type === 'unread_count') {
      console.log('📊 Unread notification count:', data.payload);
    }
  }

  // Subscribe to chat updates
  subscribe(callback: (chats: JobChat[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  private notifyListeners(): void {
    // Fetch updated chats and notify listeners
    this.getChatsForUser(this.getUserId() || '').then(chats => {
      this.listeners.forEach(listener => listener(chats));
    });
  }

  // Conversations
  async createOrGetConversation(jobId: string, providerId: string): Promise<any> {
    const response = await httpClient.post(API_CONFIG.ENDPOINTS.CONVERSATIONS, {
      job_id: jobId,
      provider_id: providerId
    }, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create conversation');
    }

    return response.data;
  }

  async getChatsForUser(userId: string): Promise<JobChat[]> {
    const chats = await this.safeApiCall(
      () => httpClient.get(API_CONFIG.ENDPOINTS.CONVERSATIONS, {
        token: this.getAuthToken(),
        headers: {
          'X-User-ID': userId
        }
      }),
      [],
      'Chat service'
    );

    return chats.map((conv: any) => this.mapConversationToChat(conv));
  }

  async getChatById(chatId: string, userId: string): Promise<JobChat | null> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.CONVERSATIONS}/${chatId}`, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': userId
      }
    });

    if (!response.success) {
      if (response.error?.includes('not found')) return null;
      throw new Error(response.error || 'Failed to fetch conversation');
    }

    return this.mapConversationToChat(response.data);
  }

  async getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.CONVERSATIONS}/${conversationId}/messages`, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch messages');
    }

    return (response.data || []).map((msg: any) => this.mapMessageFromBackend(msg));
  }

  async markConversationRead(conversationId: string): Promise<void> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.CONVERSATIONS}/${conversationId}/read`, {}, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to mark conversation as read');
    }
  }

  // Messages
  async sendMessage(
    conversationId: string,
    content: string,
    messageType: string = 'text'
  ): Promise<ChatMessage> {
    const response = await httpClient.post(API_CONFIG.ENDPOINTS.MESSAGES, {
      conversation_id: conversationId,
      content,
      message_type: messageType
    }, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to send message');
    }

    return this.mapMessageFromBackend(response.data);
  }

  async sendMessageWithFiles(
    conversationId: string,
    content: string,
    files: File[]
  ): Promise<ChatMessage> {
    // First upload files, then send message with file references
    const uploadPromises = files.map(file => this.uploadMessageFile(file));
    const uploadedFiles = await Promise.all(uploadPromises);

    const response = await httpClient.post(API_CONFIG.ENDPOINTS.MESSAGES, {
      conversation_id: conversationId,
      content: content || `Shared ${files.length} file${files.length > 1 ? 's' : ''}`,
      message_type: files.length === 1 && files[0].type.startsWith('image/') ? 'image' : 'file',
      attachments: uploadedFiles
    }, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to send message with files');
    }

    return this.mapMessageFromBackend(response.data);
  }

  private async uploadMessageFile(file: File): Promise<any> {
    // Upload file to media service (handled by Provider Service)
    // Route: POST /api/v1/auth/media/upload
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MEDIA}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    return await response.json();
  }

  // Notifications
  async getNotifications(page = 1, limit = 20): Promise<any> {
    // Build full URL for communication service notifications endpoint
    const endpoint = `/api/v1/auth/communication${API_CONFIG.ENDPOINTS.NOTIFICATIONS}`;
    console.log('🔔 Fetching notifications from:', endpoint);
    
    const response = await httpClient.get(endpoint, {
      params: { page: page.toString(), limit: limit.toString() },
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      console.error('❌ Notification fetch failed:', response.error);
      throw new Error(response.error || 'Failed to fetch notifications');
    }

    console.log('🔔 Notification response:', response);
    return response.data;
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    const endpoint = `/api/v1/auth/communication${API_CONFIG.ENDPOINTS.NOTIFICATIONS}/${notificationId}/read`;
    const response = await httpClient.put(endpoint, {}, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to mark notification as read');
    }
  }

  async markAllNotificationsRead(): Promise<void> {
    const endpoint = `/api/v1/auth/communication${API_CONFIG.ENDPOINTS.NOTIFICATIONS}/read-all`;
    const response = await httpClient.put(endpoint, {}, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to mark all notifications as read');
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    const endpoint = `/api/v1/auth/communication${API_CONFIG.ENDPOINTS.NOTIFICATIONS}/${notificationId}`;
    const response = await httpClient.delete(endpoint, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete notification');
    }
  }

  // Notification Preferences
  async getNotificationPreferences(): Promise<any> {
    const response = await httpClient.get(API_CONFIG.ENDPOINTS.NOTIFICATION_PREFERENCES, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch notification preferences');
    }

    return response.data;
  }

  async updateNotificationPreferences(preferences: any): Promise<void> {
    const response = await httpClient.put(API_CONFIG.ENDPOINTS.NOTIFICATION_PREFERENCES, preferences, {
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': this.getUserId() || ''
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update notification preferences');
    }
  }

  // Helper methods to map backend data to frontend types
  private mapConversationToChat(conv: any): JobChat {
    return {
      id: conv.id,
      jobId: conv.job_id,
      jobTitle: conv.job_title || 'Unknown Job',
      customerId: conv.customer_id,
      customerName: conv.other_participant?.first_name + ' ' + conv.other_participant?.last_name || 'Customer',
      providerId: conv.provider_id,
      providerName: conv.other_participant?.first_name + ' ' + conv.other_participant?.last_name || 'Provider',
      providerBusinessName: 'Service Provider',
      messages: [], // Messages loaded separately
      status: conv.status === 'active' ? 'active' : 'archived',
      unreadCount: conv.unread_count || 0,
      lastMessage: conv.last_message ? this.mapMessageFromBackend(conv.last_message) : undefined,
      createdAt: new Date(conv.created_at),
      updatedAt: new Date(conv.updated_at)
    };
  }

  private mapMessageFromBackend(msg: any): ChatMessage {
    return {
      id: msg.id,
      chatId: msg.conversation_id,
      senderId: msg.sender_id,
      senderName: 'User', // Would need user lookup
      senderRole: msg.sender_id === this.getUserId() ? 'customer' : 'provider',
      message: msg.content,
      messageType: msg.message_type || 'text',
      timestamp: new Date(msg.created_at),
      read: !!msg.read_at,
      attachments: msg.attachments || undefined
    };
  }

  // Utility methods
  async getTotalUnreadCount(userId: string): Promise<number> {
    const chats = await this.getChatsForUser(userId);
    return chats.reduce((total, chat) => total + chat.unreadCount, 0);
  }

  async searchMessages(userId: string, query: string): Promise<ChatMessage[]> {
    // This would need to be implemented in the backend
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.MESSAGES}/search`, {
      params: { q: query },
      token: this.getAuthToken(),
      headers: {
        'X-User-ID': userId
      }
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to search messages');
    }

    return (response.data || []).map((msg: any) => this.mapMessageFromBackend(msg));
  }
}

export const backendCommunicationService = new BackendCommunicationService();